import React, { useState } from "react";
import styled from "styled-components";
import { Input, Button, Modal, Tooltip, Upload, Typography } from "antd";
import {
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FileImageOutlined,
  FontSizeOutlined,
  AlignLeftOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title } = Typography;

const BlockContainer = styled.div`
  margin-top: 20px;

  .block-item {
    margin-bottom: 15px;
    background: #fafafa;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 15px;
    position: relative;
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .block-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      color: #666;
      font-size: 12px;
      text-transform: uppercase;
      font-weight: bold;
    }

    .block-controls {
      display: flex;
      gap: 5px;
    }
  }

  .add-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 10px;
    padding: 15px;
    background: #f0f2f5;
    border-radius: 8px;
    justify-content: center;
  }
`;

// Mobile Simulator Styles
const PreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f0f2f5;
  padding: 40px 0;
  border-radius: 12px;
`;

const MobileFrame = styled.div`
  width: 375px;
  height: 667px;
  background: white;
  border-radius: 30px;
  border: 10px solid #333;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;

  /* Notch simulation */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 25px;
    background: #333;
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
    z-index: 10;
  }

  .content-area {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding: 40px 20px 20px 20px; /* Top padding clears notch */

    /* Hide scrollbar */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  img {
    width: 100%;
    border-radius: 8px;
    margin: 10px 0;
    object-fit: cover;
  }

  h2 {
    color: #333;
    margin-top: 20px;
    font-size: 20px;
  }

  p {
    line-height: 1.6;
    color: #555;
    white-space: pre-wrap;
    font-size: 14px;
    margin-bottom: 15px;
  }

  video {
    width: 100%;
    max-height: 250px;
    background: #000;
    margin: 10px 0;
    border-radius: 8px;
  }
`;

export default function ContentBlockEditor({
  blocks,
  setBlocks,
  error,
  removeBlock,
}) {
  const [previewOpen, setPreviewOpen] = useState(false);

  const addBlock = (type) => {
    const newBlock = {
      type,
      value: "",
      order: blocks.length,
      previewUrl: "",
    };
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (index, content, isFile = false) => {
    const newBlocks = blocks.map((block, idx) => {
      if (idx === index) {
        if (isFile) {
          const url = URL.createObjectURL(content);
          return { ...block, value: content, previewUrl: url };
        }
        return { ...block, value: content };
      }
      return block;
    });
    setBlocks(newBlocks);
  };

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    if (direction === "up" && index > 0) {
      [newBlocks[index], newBlocks[index - 1]] = [
        newBlocks[index - 1],
        newBlocks[index],
      ];
    } else if (direction === "down" && index < newBlocks.length - 1) {
      [newBlocks[index], newBlocks[index + 1]] = [
        newBlocks[index + 1],
        newBlocks[index],
      ];
    }
    newBlocks.map((item, index) => {
      item.order = index;
    });
    setBlocks(newBlocks);
  };

  const renderBlockInput = (block, index) => {
    switch (block.type) {
      case "TITLE":
        return (
          <Input
            placeholder="Digite o título aqui..."
            size="large"
            value={block.value}
            onChange={(e) => updateBlock(index, e.target.value)}
            prefix={<FontSizeOutlined style={{ color: "#ccc" }} />}
          />
        );
      case "TEXT":
        return (
          <TextArea
            placeholder="Digite o texto do parágrafo..."
            rows={4}
            value={block.value}
            onChange={(e) => updateBlock(index, e.target.value)}
          />
        );
      case "IMAGE":
      case "VIDEO":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Upload
              beforeUpload={(file) => {
                updateBlock(index, file, true);
                return false;
              }}
              showUploadList={false}
              maxCount={1}
              accept={block.type === "IMAGE" ? "image/*" : "video/*"}
            >
              <Button icon={<UploadOutlined />}>
                {block.value
                  ? "Alterar Arquivo"
                  : `Selecionar ${block.type === "IMAGE" ? "Imagem" : "Vídeo"}`}
              </Button>
            </Upload>

            {block.type === "IMAGE" &&
              (block.previewUrl || block.value_url) && (
                <img
                  src={block.previewUrl || block.value_url}
                  alt="Preview"
                  style={{
                    maxHeight: 100,
                    objectFit: "contain",
                    maxWidth: 200,
                    borderRadius: 4,
                  }}
                />
              )}
            {block.type === "VIDEO" &&
              (block.previewUrl || block.value_url) && (
                <span style={{ fontSize: 12, color: "#666" }}>
                  Arquivo atual: {block.value.name || block.value}
                </span>
              )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderPreview = () => (
    <PreviewWrapper>
      <MobileFrame>
        <div className="content-area">
          {blocks.map((block) => {
            const src = block.previewUrl || block.value_url;

            switch (block.type) {
              case "TITLE":
                return <h2 key={block.id}>{block.value}</h2>;
              case "TEXT":
                return <p key={block.id}>{block.value}</p>;
              case "IMAGE":
                return src ? (
                  <img key={block.id} src={src} alt="Content" />
                ) : null;
              case "VIDEO":
                return src ? (
                  <video key={block.id} controls>
                    <source src={src} type="video/mp4" />
                    Seu navegador não suporta vídeos.
                  </video>
                ) : null;
              default:
                return null;
            }
          })}
        </div>
      </MobileFrame>
    </PreviewWrapper>
  );

  return (
    <BlockContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 15,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Conteúdo
        </Title>
        <Button
          icon={<EyeOutlined />}
          onClick={() => setPreviewOpen(true)}
          disabled={blocks.length === 0}
        >
          Pré-visualizar no Celular
        </Button>
      </div>

      {blocks.map((block, index) => (
        <div
          key={block.id}
          className="block-item"
          style={
            !block.value && error
              ? {
                  borderWidth: 1,
                  borderColor: "#F00",
                }
              : {}
          }
        >
          <div className="block-header">
            <span>
              {block.type === "IMAGE"
                ? "Imagem"
                : block.type === "VIDEO"
                  ? "Vídeo"
                  : block.type === "TITLE"
                    ? "Título"
                    : "Parágrafo"}
            </span>
            <div className="block-controls">
              <Tooltip title="Mover para cima">
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => moveBlock(index, "up")}
                />
              </Tooltip>
              <Tooltip title="Mover para baixo">
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === blocks.length - 1}
                  onClick={() => moveBlock(index, "down")}
                />
              </Tooltip>
              <Tooltip title="Remover bloco">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeBlock(block.id, index)}
                />
              </Tooltip>
            </div>
          </div>
          {renderBlockInput(block, index)}
        </div>
      ))}

      <div className="add-buttons">
        <Button icon={<FontSizeOutlined />} onClick={() => addBlock("TITLE")}>
          Título
        </Button>
        <Button icon={<AlignLeftOutlined />} onClick={() => addBlock("TEXT")}>
          Texto
        </Button>
        <Button icon={<FileImageOutlined />} onClick={() => addBlock("IMAGE")}>
          Imagem
        </Button>
        <Button icon={<PlayCircleOutlined />} onClick={() => addBlock("VIDEO")}>
          Vídeo
        </Button>
      </div>

      <Modal
        title="Pré-visualização Mobile"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={500}
        centered
      >
        {renderPreview()}
      </Modal>
    </BlockContainer>
  );
}
